import {
  Alert,
  AppShellContent,
  Button,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { MarketplaceBetaBanner } from "../components/MarketplaceBetaBanner";
import {
  marketplaceCustomersQueryOptions,
  marketplaceQueryOptions,
} from "../queries/useMarketplace";
import { HomeScreen } from "../screens/HomeScreen";

function MarketplacePageFrame({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <AppShellContent
      banner={<MarketplaceBetaBanner />}
      pageHeader={
        <Stack gap={8}>
          <Title size="lg">{t("marketplace.title")}</Title>
          <Text tone="subtle">{t("marketplace.pageDescription")}</Text>
        </Stack>
      }
    >
      {children}
    </AppShellContent>
  );
}

function MarketplaceHomeRoute() {
  const navigate = useNavigate();
  const { externalCustomerId, sourceApp } = Route.useSearch();

  return (
    <MarketplacePageFrame>
      <HomeScreen
        externalCustomerId={externalCustomerId}
        onCustomerChange={(nextExternalCustomerId) => {
          // facilityIds belong to the previous customer, so a customer change
          // intentionally drops them instead of linking legacy to the wrong sites.
          void navigate({
            to: "/",
            search: {
              ...(nextExternalCustomerId
                ? { externalCustomerId: nextExternalCustomerId }
                : {}),
              ...(sourceApp ? { sourceApp } : {}),
            },
          });
        }}
      />
    </MarketplacePageFrame>
  );
}

function MarketplaceHomeRouteError() {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <MarketplacePageFrame>
      <Alert
        tone="negative"
        title={t("marketplace.loadErrorTitle")}
        subtext={t("marketplace.loadErrorDescription")}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void router.invalidate()}
          >
            {t("common.tryAgain")}
          </Button>
        }
      />
    </MarketplacePageFrame>
  );
}

export const Route = createFileRoute("/_app/")({
  validateSearch: z.object({
    externalCustomerId: z.coerce.string().trim().min(1).optional(),
    sourceApp: z.enum(["legacy", "enterprise", "facility"]).optional(),
    facilityIds: z.coerce.string().trim().min(1).optional(),
  }),
  loaderDeps: ({ search }) => ({
    externalCustomerId: search.externalCustomerId,
  }),
  loader: async ({ context, deps }) => {
    const { customers } = await context.queryClient.ensureQueryData(
      marketplaceCustomersQueryOptions(),
    );
    const { externalCustomerId } = deps;
    // Load Marketplace data only for a customer the user can access (or their
    // only customer); otherwise HomeScreen asks them to choose one.
    const canLoadMarketplace = externalCustomerId
      ? customers.some(
          (customer) => customer.externalCustomerId === externalCustomerId,
        )
      : customers.length === 1;
    if (canLoadMarketplace) {
      await context.queryClient.ensureQueryData(
        marketplaceQueryOptions(externalCustomerId),
      );
    }
  },
  errorComponent: MarketplaceHomeRouteError,
  component: MarketplaceHomeRoute,
});
